import { useState, useEffect, useCallback, useRef } from 'react';
import { config } from '../config';
import { standaloneWebSocketService, TranscriptData } from '../services/standaloneWebSocketService';

interface StandaloneSessionData {
  sessionId: string;
  roomId: string;
  sessionName: string;
  startedAt: string;
  userEmail: string;
}

interface UseStandaloneSessionOptions {
  enabled?: boolean;
  token: string | null;
  onTranscript?: (data: TranscriptData) => void;
  onAlert?: (type: string, data: any) => void;
}

interface UseStandaloneSessionReturn {
  isLobbyConnected: boolean;
  isSessionConnected: boolean;
  activeSession: StandaloneSessionData | null;
  error: string | null;
  reconnectLobby: () => void;
}

export const useStandaloneSession = (
  options: UseStandaloneSessionOptions
): UseStandaloneSessionReturn => {
  const { enabled = true, token, onTranscript, onAlert } = options;

  const [isLobbyConnected, setIsLobbyConnected] = useState(false);
  const [isSessionConnected, setIsSessionConnected] = useState(false);
  const [activeSession, setActiveSession] = useState<StandaloneSessionData | null>(null);
  const [error, setError] = useState<string | null>(null);

  const lobbyWsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const isMountedRef = useRef(true);
  const isConnectingRef = useRef(false);
  const tokenRef = useRef(token);
  const maxReconnectAttempts = 5;

  // Keep tokenRef updated
  useEffect(() => {
    tokenRef.current = token;
  }, [token]);

  // Cleanup function for lobby WebSocket
  const cleanupLobby = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (lobbyWsRef.current) {
      // Remove handlers before closing to prevent reconnect attempts
      lobbyWsRef.current.onclose = null;
      lobbyWsRef.current.onerror = null;
      lobbyWsRef.current.onmessage = null;
      lobbyWsRef.current.onopen = null;
      lobbyWsRef.current.close(1000, 'Cleanup');
      lobbyWsRef.current = null;
    }
    setIsLobbyConnected(false);
    isConnectingRef.current = false;
  }, []);

  // Connect to session WebSocket when we have a session
  const connectToSession = useCallback(async (sessionId: string) => {
    const currentToken = tokenRef.current;
    if (!currentToken) return;

    console.log('[useStandaloneSession] Connecting to session:', sessionId);

    const connected = await standaloneWebSocketService.connect(sessionId, currentToken);

    if (isMountedRef.current) {
      setIsSessionConnected(connected);
      if (!connected) {
        setError('Failed to connect to session');
      }
    }
  }, []);

  // Disconnect from session
  const disconnectFromSession = useCallback(() => {
    standaloneWebSocketService.disconnect();
    setIsSessionConnected(false);
  }, []);

  // Connect to lobby WebSocket
  const connectLobby = useCallback(() => {
    const currentToken = tokenRef.current;
    if (!currentToken || !enabled) return;

    // Prevent multiple simultaneous connection attempts
    if (isConnectingRef.current) {
      console.log('[useStandaloneSession] Already connecting, skipping');
      return;
    }

    // If already connected with the same WebSocket, don't reconnect
    if (lobbyWsRef.current && lobbyWsRef.current.readyState === WebSocket.OPEN) {
      console.log('[useStandaloneSession] Already connected to lobby');
      return;
    }

    // Clean up any existing connection first
    cleanupLobby();

    isConnectingRef.current = true;

    const wsUrl = config.server.standaloneWsUrl || `ws://localhost:${config.server.standalonePort}`;
    const url = `${wsUrl}/ws/user?token=${currentToken}`;

    console.log('[useStandaloneSession] Connecting to lobby:');

    try {
      const ws = new WebSocket(url);
      lobbyWsRef.current = ws;

      ws.onopen = () => {
        if (!isMountedRef.current) {
          ws.close(1000, 'Component unmounted');
          return;
        }
        console.log('[useStandaloneSession] Lobby connected');
        isConnectingRef.current = false;
        setIsLobbyConnected(true);
        setError(null);
        reconnectAttemptsRef.current = 0;
      };

      ws.onmessage = (event) => {
        if (!isMountedRef.current) return;

        try {
          const message = JSON.parse(event.data);
          console.log('[useStandaloneSession] Lobby message:', message.type);

          switch (message.type) {
            case 'connection_established':
              // Connected but no active session
              setActiveSession(null);
              break;

            case 'standalone_session_active':
            case 'standalone_session_started':
              // Desktop app has an active session or just started one
              const sessionData: StandaloneSessionData = {
                sessionId: message.data.session_id,
                roomId: message.data.room_id,
                sessionName: message.data.session_name,
                startedAt: message.data.started_at,
                userEmail: message.data.user_email
              };
              setActiveSession(sessionData);
              // Automatically connect to session for live updates
              connectToSession(sessionData.sessionId);
              break;

            case 'standalone_session_ended':
              // Desktop app ended the session
              console.log('[useStandaloneSession] Session ended:', message.data.session_id);
              setActiveSession(null);
              disconnectFromSession();
              break;

            case 'no_active_session':
              setActiveSession(null);
              break;

            default:
              console.log('[useStandaloneSession] Unknown lobby message:', message.type);
          }
        } catch (err) {
          console.error('[useStandaloneSession] Error parsing lobby message:', err);
        }
      };

      ws.onclose = (event) => {
        if (!isMountedRef.current) return;

        console.log('[useStandaloneSession] Lobby disconnected:', event.code, event.reason);
        setIsLobbyConnected(false);
        isConnectingRef.current = false;
        lobbyWsRef.current = null;

        // Attempt reconnect if not intentional close and component is still mounted
        if (event.code !== 1000 && enabled && reconnectAttemptsRef.current < maxReconnectAttempts) {
          reconnectAttemptsRef.current++;
          const delay = Math.min(1000 * Math.pow(2, reconnectAttemptsRef.current), 30000);
          console.log(`[useStandaloneSession] Reconnecting lobby in ${delay}ms (attempt ${reconnectAttemptsRef.current})`);
          reconnectTimeoutRef.current = setTimeout(() => {
            if (isMountedRef.current) {
              connectLobby();
            }
          }, delay);
        }
      };

      ws.onerror = (event) => {
        if (!isMountedRef.current) return;
        console.error('[useStandaloneSession] Lobby WebSocket error:', event);
        isConnectingRef.current = false;
        setError('Lobby connection error');
      };
    } catch (err) {
      console.error('[useStandaloneSession] Failed to create lobby WebSocket:', err);
      isConnectingRef.current = false;
      setError('Failed to connect to lobby');
    }
  }, [enabled, cleanupLobby, connectToSession, disconnectFromSession]);

  // Setup transcript and alert callbacks
  useEffect(() => {
    const unsubscribeTranscript = onTranscript
      ? standaloneWebSocketService.onTranscript(onTranscript)
      : () => {};

    const unsubscribeAlert = onAlert
      ? standaloneWebSocketService.onAlert(onAlert)
      : () => {};

    const unsubscribeConnection = standaloneWebSocketService.onConnectionChange((connected) => {
      setIsSessionConnected(connected);
    });

    return () => {
      unsubscribeTranscript();
      unsubscribeAlert();
      unsubscribeConnection();
    };
  }, [onTranscript, onAlert]);

  // Connect to lobby on mount and when token/enabled changes
  useEffect(() => {
    isMountedRef.current = true;

    if (enabled && token) {
      // Small delay to avoid issues with React StrictMode double-invoke
      const timeoutId = setTimeout(() => {
        if (isMountedRef.current) {
          connectLobby();
        }
      }, 100);

      return () => {
        clearTimeout(timeoutId);
        isMountedRef.current = false;
        cleanupLobby();
        disconnectFromSession();
      };
    }

    return () => {
      isMountedRef.current = false;
      cleanupLobby();
      disconnectFromSession();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, token]);

  const reconnectLobby = useCallback(() => {
    reconnectAttemptsRef.current = 0;
    connectLobby();
  }, [connectLobby]);

  return {
    isLobbyConnected,
    isSessionConnected,
    activeSession,
    error,
    reconnectLobby
  };
};
